/**
 * The activity bar's Files view (core DriveViewModel): "+ New", the views (My files, Shared with me, Recent, Starred,
 * Trash, From conversations), my top-level folders, the workspace storage meter, and "Sync to this computer" (desktop
 * app) or "Get the desktop app" (desktop browser).
 */
import { Shell, type OpenTarget } from "@bundle/core";
import { Button, DriveMeter, SidebarNavRow, Stack, Text, useDrive, useT, useWorkspace } from "@bundle/ui";
import { useEffect } from "react";
import { DesktopSyncEntry } from "./DesktopSyncEntry.tsx";
import { filesUrl, NewMenu, percentUsed, useSize, VIEW_ICONS } from "../screens/files/filesUi.tsx";

export function SidebarFiles({ activeUrl, open }: { activeUrl: string; open: (url: string, target?: OpenTarget) => void }) {
  const t = useT();
  const ws = useWorkspace();
  const drive = useDrive();
  const size = useSize();
  useEffect(() => {
    void drive?.vm.loadSidebar();
  }, [drive?.vm]);
  if (drive === null) return null;
  const { state } = drive;
  const active = Shell.parseUrl(activeUrl);
  const route = active?.type === "files" ? active : undefined;
  const folderOpen = route?.folderId;
  const pathIds = new Set(state.path.map((p) => p.id));
  const st = state.storage;
  return (
    <>
      <div className="sidebar-view-header">
        <Text variant="headline" weight="semibold">
          {t("drive.title")}
        </Text>
      </div>
      <Stack paddingX="3" paddingY="1">
        <NewMenu variant="primary" testID="files-new" open={open} />
      </Stack>
      <div className="sidebar-group">
        {Shell.DRIVE_VIEWS.map((v) => (
          <SidebarNavRow
            key={v}
            icon={VIEW_ICONS[v]}
            label={t(`drive.view.${v}`)}
            selected={route !== undefined && route.folderId === undefined && route.itemId === undefined && (route.q ?? "") === "" && (route.view ?? "my") === v}
            onPress={() => open(filesUrl(ws.slug, { view: v }))}
            testID={`nav-files-${v}`}
          />
        ))}
      </div>
      {state.tree.length === 0 ? null : (
        <div className="sidebar-group" data-testid="sidebar-files-folders">
          <div className="sidebar-view-label">
            <Text variant="caption" weight="semibold" color="text.tertiary">
              {t("drive.section.folders")}
            </Text>
          </div>
          {state.tree.map((f) => (
            <SidebarNavRow
              key={f.id}
              icon="folder"
              label={f.name}
              selected={folderOpen === f.id || (folderOpen !== undefined && pathIds.has(f.id) && state.location.folderId === folderOpen)}
              onPress={() => open(filesUrl(ws.slug, { folderId: f.id }))}
              testID={`nav-files-folder-${f.id}`}
            />
          ))}
        </div>
      )}
      {st === undefined ? null : (
        <Stack paddingX="4" paddingY="3" gap="1.5" testID="files-storage">
          <Text variant="caption" weight="semibold" color="text.secondary">
            {t("drive.storage")}
          </Text>
          <DriveMeter
            percent={percentUsed(st.used, st.quota)}
            level={state.storageLevel}
            label={t("drive.storage.meter", { percent: percentUsed(st.used, st.quota) })}
          />
          <Text variant="caption" color="text.tertiary">
            {t("drive.storage.used", {
              used: size(st.used),
              quota: size(st.quota),
            })}
          </Text>
          <Stack direction="horizontal">
            <Button
              label={t("drive.storage.manage")}
              variant="ghost"
              size="sm"
              onPress={() => open(filesUrl(ws.slug, { view: "trash" }))}
              testID="files-storage-manage"
            />
          </Stack>
        </Stack>
      )}
      <DesktopSyncEntry workspaceSlug={ws.slug} />
    </>
  );
}
